import { useEffect, useState } from "react";
import api, { formatError, formatRupiah } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Plus, Trash2, Wallet, Loader2, ArrowUpRight, ArrowDownRight } from "lucide-react";

export default function Kas() {
  const [entries, setEntries] = useState(null);
  const [sources, setSources] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ type: "masuk", amount: "", description: "", source_id: "none" });
  const [saving, setSaving] = useState(false);

  const fetchEntries = () => {
    Promise.all([api.get("/kas"), api.get("/money-sources")])
      .then(([k, m]) => { setEntries(k.data); setSources(m.data); })
      .catch(() => setEntries([]));
  };
  useEffect(() => { fetchEntries(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/kas", { type: form.type, amount: parseFloat(form.amount) || 0, description: form.description, source_id: form.source_id === "none" ? null : form.source_id });
      toast.success("Transaksi berhasil dicatat");
      setFormOpen(false);
      setForm({ type: "masuk", amount: "", description: "", source_id: "none" });
      fetchEntries();
    } catch (err) {
      toast.error(formatError(err, "Gagal menyimpan transaksi"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (entry) => {
    try {
      await api.delete(`/kas/${entry.id}`);
      toast.success("Transaksi dihapus");
      fetchEntries();
    } catch (err) {
      toast.error(formatError(err, "Gagal menghapus transaksi"));
    }
  };

  const saldo = (entries || []).reduce((acc, e) => acc + (e.type === "masuk" ? e.amount : -e.amount), 0);
  const selectedSource = sources.find((s) => s.id === form.source_id);
  const amountNum = parseFloat(form.amount) || 0;

  if (entries === null) {
    return (
      <div className="flex items-center justify-center py-24" data-testid="kas-loading">
        <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="kas-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Kas & Transaksi</h1>
          <p className="text-sm text-slate-500 mt-1">Catat pemasukan dan pengeluaran kas Anda</p>
        </div>
        <Button onClick={() => setFormOpen(true)} data-testid="add-transaction-button" className="bg-indigo-600 hover:bg-indigo-700 text-white transition-colors duration-200">
          <Plus className="w-4 h-4 mr-2" /> Tambah Transaksi
        </Button>
      </div>

      <Card className="border-slate-200 shadow-sm" data-testid="saldo-card">
        <CardContent className="p-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center shrink-0">
            <Wallet className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Saldo Kas Saat Ini</p>
            <p className="text-2xl font-heading font-bold text-slate-900" data-testid="kas-saldo">{formatRupiah(saldo)}</p>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200 shadow-sm overflow-hidden" data-testid="kas-table-card">
        <CardContent className="p-0">
          <div className="overflow-auto">
            <Table data-testid="kas-table">
              <TableHeader>
                <TableRow className="bg-slate-50/50 border-b border-slate-200">
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Tanggal</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Keterangan</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Jenis</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Sumber/Tujuan</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Jumlah</TableHead>
                  <TableHead className="text-xs font-semibold text-slate-500 uppercase tracking-wider text-right">Aksi</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-16" data-testid="kas-empty">
                      <Wallet className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                      <p className="text-sm text-slate-400">Belum ada transaksi. Klik Tambah Transaksi untuk mulai.</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((entry) => (
                    <TableRow key={entry.id} className="border-b border-slate-100 hover:bg-slate-50/50 transition-colors duration-200" data-testid={`kas-row-${entry.id}`}>
                      <TableCell className="p-4 text-sm text-slate-500">{new Date(entry.created_at).toLocaleDateString("id-ID", { day: "numeric", month: "short", year: "numeric" })}</TableCell>
                      <TableCell className="p-4 text-sm font-medium text-slate-800">{entry.description}</TableCell>
                      <TableCell className="p-4">
                        <Badge className={entry.type === "masuk" ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-50" : "bg-rose-50 text-rose-600 hover:bg-rose-50"} data-testid={`kas-type-${entry.id}`}>
                          <span className="flex items-center gap-1">
                            {entry.type === "masuk" ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                            {entry.type === "masuk" ? "Pemasukan" : "Pengeluaran"}
                          </span>
                        </Badge>
                      </TableCell>
                      <TableCell className="p-4">
                        {entry.source_name ? (
                          <Badge variant="secondary" className="bg-indigo-50 text-indigo-600 hover:bg-indigo-50" data-testid={`kas-source-${entry.id}`}>{entry.source_name}</Badge>
                        ) : (
                          <span className="text-sm text-slate-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className={`p-4 text-sm font-semibold text-right ${entry.type === "masuk" ? "text-emerald-600" : "text-rose-600"}`} data-testid={`kas-amount-${entry.id}`}>
                        {entry.type === "masuk" ? "+" : "-"}{formatRupiah(entry.amount)}
                      </TableCell>
                      <TableCell className="p-4 text-right">
                        <Button
                          variant="ghost" size="icon"
                          data-testid={`delete-kas-button-${entry.id}`}
                          onClick={() => handleDelete(entry)}
                          className="text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={formOpen} onOpenChange={(open) => { setFormOpen(open); if (!open) setForm({ type: "masuk", amount: "", description: "", source_id: "none" }); }}>
        <DialogContent className="sm:max-w-md" data-testid="kas-form-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Tambah Transaksi</DialogTitle>
            <DialogDescription>Catat pemasukan atau pengeluaran kas.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="kas-form">
            <div className="space-y-2">
              <Label>Jenis Transaksi</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="kas-type-select" className="focus:ring-indigo-500">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="masuk" data-testid="kas-type-masuk">Pemasukan</SelectItem>
                  <SelectItem value="keluar" data-testid="kas-type-keluar">Pengeluaran</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{form.type === "masuk" ? "Masuk Ke (Tujuan Uang)" : "Diambil Dari (Sumber Uang)"}</Label>
              <Select value={form.source_id} onValueChange={(v) => setForm({ ...form, source_id: v })}>
                <SelectTrigger data-testid="kas-source-select" className="focus:ring-indigo-500">
                  <SelectValue placeholder="Pilih tempat uang (opsional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" data-testid="kas-source-none">Tidak dicatat ke tempat uang</SelectItem>
                  {sources.map((s) => (
                    <SelectItem key={s.id} value={s.id} data-testid={`kas-source-option-${s.id}`}>{s.name} ({formatRupiah(s.balance)})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {form.type === "keluar" && selectedSource && amountNum > selectedSource.balance && (
                <p className="text-xs text-rose-600" data-testid="kas-insufficient-hint">Saldo {selectedSource.name} tidak cukup untuk pengeluaran ini</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="amount">Jumlah (Rp)</Label>
              <Input id="amount" type="number" min="1" data-testid="kas-amount-input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required className="focus-visible:ring-indigo-500" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="desc">Keterangan</Label>
              <Input id="desc" data-testid="kas-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Contoh: Penjualan barang" required className="focus-visible:ring-indigo-500" />
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" data-testid="kas-form-cancel" onClick={() => setFormOpen(false)}>Batal</Button>
              <Button type="submit" data-testid="kas-form-submit" disabled={saving || (form.type === "keluar" && selectedSource && amountNum > selectedSource.balance)} className="bg-indigo-600 hover:bg-indigo-700 text-white transition-colors">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Simpan Transaksi"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
